/**
 * security.ts - Security utilities for preventing XSS attacks
 */

/**
 * Escapes HTML special characters to prevent XSS attacks
 * @param str - The string to escape
 * @returns The escaped string safe for HTML insertion
 */
export function escapeHtml(str: string | null | undefined): string {
  if (str === null || str === undefined) return '';

  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Sanitizes a string by removing all HTML tags
 * @param str - The string to sanitize
 * @returns String with HTML tags removed
 */
export function stripHtml(str: string | null | undefined): string {
  if (str === null || str === undefined) return '';

  const div = document.createElement('div');
  div.innerHTML = str;
  return div.textContent || div.innerText || '';
}

/**
 * Escapes HTML attributes to prevent XSS in attribute values
 * @param str - The string to escape
 * @returns The escaped string safe for HTML attributes
 */
export function escapeHtmlAttr(str: string | null | undefined): string {
  if (str === null || str === undefined) return '';

  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Validates that a string is safe (alphanumeric with basic punctuation)
 * @param str - The string to validate
 * @returns Whether the string is safe
 */
export function isSafeString(str: string | null | undefined): boolean {
  if (str === null || str === undefined) return false;

  // Allow alphanumeric, spaces, and basic punctuation
  const safePattern = /^[a-zA-Z0-9\s.,!?()'\-]+$/;
  return safePattern.test(str);
}

// Export to window for global access

window.escapeHtml = escapeHtml;
window.stripHtml = stripHtml;
window.escapeHtmlAttr = escapeHtmlAttr;
window.isSafeString = isSafeString;
